<script setup>
import { computed, useTemplateRef, ref, onMounted } from 'vue';
import { Letter } from 'vue-letter';
import { sanitizeTextForRender } from '@chatwoot/utils';
import { allowedCssProperties } from 'lettersanitizer';

import Icon from 'next/icon/Icon.vue';
import Avatar from 'next/avatar/Avatar.vue';
import { EmailQuoteExtractor } from 'dashboard/helper/emailQuoteExtractor.js';
import FormattedContent from 'next/message/bubbles/Text/FormattedContent.vue';
import BaseBubble from 'next/message/bubbles/Base.vue';
import AttachmentChips from 'next/message/chips/AttachmentChips.vue';
import EmailMeta from './EmailMeta.vue';
import TranslationToggle from 'dashboard/components-next/message/TranslationToggle.vue';

import { useMessageContext } from '../../provider.js';
import { useMessageSender } from '../../useMessageSender.js';
import { MESSAGE_TYPES } from 'next/message/constants.js';
import { useTranslations } from 'dashboard/composables/useTranslations';

const { content, contentAttributes, attachments, messageType } =
  useMessageContext();
const { name: senderAvatarName, avatarUrl: senderAvatarUrl } =
  useMessageSender();

const isExpandable = ref(false);
const isExpanded = ref(false);
const showQuotedMessage = ref(false);
const renderOriginal = ref(false);
const contentContainer = useTemplateRef('contentContainer');

onMounted(() => {
  // Bloom: don't auto-collapse short-to-medium emails. Only fold the
  // really-long ones (most threaded conversations don't need this).
  isExpandable.value = contentContainer.value?.scrollHeight > 900;
});

const isOutgoing = computed(() => messageType.value === MESSAGE_TYPES.OUTGOING);

const { hasTranslations, translationContent } =
  useTranslations(contentAttributes);

const originalEmailText = computed(() => {
  const text =
    contentAttributes?.value?.email?.textContent?.full ?? content.value;
  return sanitizeTextForRender(text);
});

const originalEmailHtml = computed(
  () =>
    contentAttributes?.value?.email?.htmlContent?.full ||
    originalEmailText.value
);

const hasEmailContent = computed(() => {
  return (
    contentAttributes?.value?.email?.textContent?.full ||
    contentAttributes?.value?.email?.htmlContent?.full
  );
});

const messageContent = computed(() => {
  // If translations exist and we're showing translations (not original)
  if (hasTranslations.value && !renderOriginal.value) {
    return translationContent.value;
  }
  // Otherwise show original content
  return content.value;
});

const textToShow = computed(() => {
  // If translations exist and we're showing translations (not original)
  if (hasTranslations.value && !renderOriginal.value) {
    return translationContent.value;
  }
  // Otherwise show original text
  return originalEmailText.value;
});

const fullHTML = computed(() => {
  // If translations exist and we're showing translations (not original)
  if (hasTranslations.value && !renderOriginal.value) {
    return translationContent.value;
  }
  // Otherwise show original HTML
  return originalEmailHtml.value;
});

const unquotedHTML = computed(() =>
  EmailQuoteExtractor.extractQuotes(fullHTML.value)
);

const hasQuotedMessage = computed(() =>
  EmailQuoteExtractor.hasQuotes(fullHTML.value)
);

// Ensure unique keys for <Letter> when toggling between original and translated views.
// This forces Vue to re-render the component and update content correctly.
const translationKeySuffix = computed(() => {
  if (renderOriginal.value) return 'original';
  if (hasTranslations.value) return 'translated';
  return 'original';
});

const handleSeeOriginal = () => {
  renderOriginal.value = !renderOriginal.value;
};
</script>

<template>
  <!-- Bloom: Front-style email card. Single uniform surface — sender row
       at top, body below, no internal tinted band. The card boundary +
       small shadow is the only separation between messages. -->
  <BaseBubble
    class="w-full bg-white border border-n-slate-3 rounded-lg overflow-hidden shadow-sm px-4 py-3 transition-shadow hover:shadow-md focus-within:ring-1 focus-within:ring-n-slate-6"
    data-bubble-name="email"
  >
    <header class="flex items-start gap-3 pb-3">
      <Avatar
        :name="senderAvatarName"
        :src="senderAvatarUrl"
        :size="32"
        rounded-full
        class="shrink-0 mt-0.5"
      />
      <div class="flex-1 min-w-0">
        <EmailMeta />
      </div>
    </header>
    <div>
      <section ref="contentContainer">
        <div
          :class="{
            'max-h-[900px] overflow-hidden relative':
              !isExpanded && isExpandable,
            'overflow-y-scroll relative': isExpanded,
          }"
        >
          <div
            v-if="isExpandable && !isExpanded"
            class="absolute left-0 right-0 bottom-0 h-40 px-8 flex items-end bg-gradient-to-t from-n-alpha-1 via-n-alpha-1 via-20% to-transparent"
          >
            <button
              class="text-n-slate-12 py-2 px-8 mx-auto text-center flex items-center gap-2"
              @click="isExpanded = true"
            >
              <Icon icon="i-lucide-maximize-2" />
              {{ $t('EMAIL_HEADER.EXPAND') }}
            </button>
          </div>
          <FormattedContent
            v-if="isOutgoing && content && !hasEmailContent"
            class="text-n-slate-12"
            :content="messageContent"
          />
          <template v-else>
            <Letter
              v-if="showQuotedMessage"
              :key="`letter-quoted-${translationKeySuffix}`"
              class-name="prose prose-bubble !max-w-none letter-render"
              :allowed-css-properties="[
                ...allowedCssProperties,
                'transform',
                'transform-origin',
              ]"
              :allowed-schemas="['http', 'https', 'mailto', 'data', 'cid']"
              :html="fullHTML"
              :text="textToShow"
            />
            <Letter
              v-else
              :key="`letter-unquoted-${translationKeySuffix}`"
              class-name="prose prose-bubble !max-w-none letter-render"
              :html="unquotedHTML"
              :allowed-css-properties="[
                ...allowedCssProperties,
                'transform',
                'transform-origin',
              ]"
              :allowed-schemas="['http', 'https', 'mailto', 'data', 'cid']"
              :text="textToShow"
            />
          </template>
          <button
            v-if="hasQuotedMessage"
            class="text-n-slate-11 px-1 leading-none text-sm bg-n-alpha-black2 text-center flex items-center gap-1 mt-2"
            @click="showQuotedMessage = !showQuotedMessage"
          >
            <template v-if="showQuotedMessage">
              {{ $t('CHAT_LIST.HIDE_QUOTED_TEXT') }}
            </template>
            <template v-else>
              {{ $t('CHAT_LIST.SHOW_QUOTED_TEXT') }}
            </template>
            <Icon
              :icon="
                showQuotedMessage
                  ? 'i-lucide-chevron-up'
                  : 'i-lucide-chevron-down'
              "
            />
          </button>
        </div>
      </section>
      <TranslationToggle
        v-if="hasTranslations"
        class="py-2"
        :showing-original="renderOriginal"
        @toggle="handleSeeOriginal"
      />
      <section
        v-if="Array.isArray(attachments) && attachments.length"
        class="pt-2 space-y-2"
      >
        <AttachmentChips :attachments="attachments" class="gap-1" />
      </section>
    </div>
  </BaseBubble>
</template>

<style lang="scss">
// Tailwind resets break the rendering of google drive link in Gmail messages
// This fixes it using https://developer.mozilla.org/en-US/docs/Web/CSS/Attribute_selectors

.letter-render [class*='gmail_drive_chip'] {
  box-sizing: initial;
  @apply bg-n-slate-4 border-n-slate-6 rounded-md !important;

  a {
    @apply text-n-slate-12 !important;

    img {
      display: inline-block;
    }
  }
}

// Email clients (Gmail, Outlook) hardcode dir="ltr" on wrapper elements.
// In RTL apps this forces email content LTR regardless of actual text.
[dir='rtl'] .letter-render [dir='ltr'] {
  direction: inherit;
}
</style>
